<template>
    <div class="relative">
        <div class="mb-6 flex items-center justify-between">
            <div class="space-y-2">
                <ULink to="/invoices" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700 py-1">
                    <span>Invoices</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="invoice" class="flex items-center space-x-4">
                    <h1 class="text-3xl font-semibold text-gray-700">Invoice #{{ invoice.number.toString().padStart(6, '0') }}</h1>
                    <BadgeInvoiceStatus :invoice="invoice" />
                </div>
            </div>

            <div class="flex items-center gap-x-2">
                <template v-if="!invoice?.settled_at && !invoice?.voided_at">
                    <UButton
                        @click="overlays.settle = true"
                        label="Settle"
                        color="emerald"
                        icon="i-ph-check-fill"
                    />
                    <UButton
                        @click="overlays.void = true"
                        label="Void"
                        color="red"
                        icon="i-ph-x-fill"
                    />
                </template>
                <UButton
                    v-if="!invoice?.voided_at"
                    @click="exportInvoice()" 
                    :loading="exporting"
                    label="Export"
                    color="black"
                    icon="i-ph-export-bold"
                />
            </div>
        </div>

        <div v-if="invoice" class="relative flex items-start gap-4">
            <div class="sticky top-0 flex-shrink-0 w-96 space-y-4">
                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Details</h3>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <Field label="ID">
                            <span class="font-mono">{{ invoice.id.split('-').at(-1)?.toUpperCase() }}</span>
                        </Field>
                        <Field v-if="invoice?.partner" label="Partner">
                            <div class="flex items-center space-x-2.5 mt-1.5">
                                <Avatar bucket="media" folder="partners" size="sm" :placeholder="invoice.partner.name" :path="invoice.partner.avatar" />
                                <span>{{ invoice.partner.name }}</span>
                            </div>
                        </Field>
                        <div class="grid grid-cols-2 gap-2">
                            <Field label="Subscriptions Count">
                                <span class="font-mono">
                                    {{ invoice.subscriptions?.length || '–' }}
                                </span>
                            </Field>
                            <Field label="Subscription Price">
                                <span class="font-mono">
                                    ${{ ((invoice.unit_price || 0) / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                </span>
                            </Field>
                        </div>
                        <Field label="Total Amount">
                            <span class="font-mono">
                                ${{ ((invoice.total || 0) / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                            </span>
                        </Field>
                        <Field label="Created">
                            <span>{{ dayjs(invoice.created_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                        <Field v-if="invoice.settled_at" label="Settled On">
                            <span>{{ dayjs(invoice.settled_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                        <Field v-if="invoice.voided_at" label="Voided On">
                            <span>{{ dayjs(invoice.voided_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                    </div>
                </div>
            </div>
            <div class="flex-1">
                <div class="flex flex-col bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Subscriptions</h3>
                    </div>

                    <UTable 
                        :rows="paginatedSubscriptions" 
                        :columns="tableColumns" 
                        :loading="loading"
                        :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                        :empty-state="{ icon: 'i-ph-shield-duotone', label: 'No subscriptions.' }"
                        :ui="{ th: { padding: 'whitespace-nowrap' }, td: { padding: 'py-3' } }"
                    >
                        <template #id-data="{ row }">
                            <p class="text-sm">
                                {{ row.subscription_id.split('-').at(-1)?.toUpperCase() }}
                            </p>
                        </template>
                        <template #customer-data="{ row }">
                            <p class="text-sm">
                                {{ row.customer_name }}
                            </p>
                        </template>
                        <template #policy_holder-data="{ row }">
                            <p class="text-sm">
                                {{ row.customer_policy_holder || '–' }}
                            </p>
                        </template>
                        <template #price-data="{ row }">
                            <p class="font-mono text-sm text-right">
                                ${{ ((row.subscription_unit_price_cents || 0) / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                            </p>
                        </template>
                        <template #validity-data="{ row }">
                            <p class="font-mono text-sm text-right">
                                {{ row.subscription_validity_months }}
                            </p>
                        </template>
                        <template #starts-data="{ row }">
                            <TableDateCell :date="row.subscription_created_date" without-time class="text-sm" />
                        </template>
                        <template #expiry-data="{ row }">
                            <TableDateCell :date="row.subscription_expiry_date" without-time class="text-sm" />
                        </template>
                    </UTable>
                </div>

                <TablePagination :pagination="pagination" class="shrink-0 mt-4" />
            </div>
        </div>

        <OverlayInvoiceSettleInvoice v-model:open="overlays.settle" @reload="fetchData" :invoice="invoice" />
        <OverlayInvoiceVoidInvoice v-model:open="overlays.void" @reload="fetchData" :invoice="invoice" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import Avatar from '~/components/global/Avatar.vue'
import type { Invoice } from '~/types/invoice'
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'View Invoice' })

const route = useRoute()
const client = useSupabaseClient()
const pagination = usePagination()

const loading = ref(false)
const exporting = ref(false)
const invoice = ref<Invoice>()

const overlays = reactive({
    settle: false,
    void: false,
})

const paginatedSubscriptions = computed(() => {
    const start = pagination.page * pagination.limit
    const end = (pagination.page + 1) * pagination.limit
    return invoice.value?.subscriptions?.slice(start, end) || []
})

const tableColumns = [
    { key: 'id', label: 'ID' },
    { key: 'customer', label: 'Customer' },
    { key: 'policy_holder', label: 'Policy Holder', class: 'w-full' },
    { key: 'validity', label: 'Remaining Months' },
    { key: 'price', label: 'Unit Price' },
    { key: 'starts', label: 'Start Date' },
    { key: 'expiry', label: 'Expiry Date' },
]

onMounted(() => fetchData())

async function fetchData() {
    loading.value = true

    const { data, error } = await client
        .from('invoices')
        .select(`
            id, number, partner_id, unit_price, settled_at, voided_at, created_at, updated_at,
            partner:partners (id, name, avatar)
        `)
        .eq('id', route.params.id)
        .single<Invoice>()

    if (data) {
        invoice.value = data

        const subscriptions = await $fetch(`/api/invoices/${data.id}/subscriptions`)

        if (subscriptions) {
            invoice.value.subscriptions = subscriptions
            invoice.value.total = subscriptions.reduce((sum: number, s: any) => sum + s.subscription_unit_price_cents, 0) || 0
        }

        pagination.setTotal(invoice.value.subscriptions?.length || 0)
    } else if (error) {
        console.error(error)
    }

    loading.value = false
}

async function exportInvoice() {
    if (invoice.value?.subscriptions.length) {
        exporting.value = true

        const csvData = await $fetch(`/api/invoices/${invoice.value.id}/subscriptions.csv`)

        if (csvData) {
            const blob = new Blob([csvData], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            const filename = `invoice-${invoice.value?.number.toString().padStart(6, '0')}.csv`
            a.style.display = 'none'
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        }

        exporting.value = false
    }
}
</script>
